import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import Button from "../Components/Button";
import ReportsLayout from "./ReportsLayout";
import { FilterContext } from "../FilterContextProvider";
import ReactToPrint from "react-to-print";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { exportToCSV } from "../Utility/Csv";
import { UserContext } from "../Auth/UserContextProvider";
import Select from "react-select";
import useApi from "../Api/Api";
import useDateSelections from "../hooks/useDateSelections";

const useArtistsPerformanceReport = (selectedArtists, selectedEventTypes) => {
  const api = useApi();
  const [report, setReport] = useState({});
  const [isFetching, setIsFetching] = useState(true);
  const [errorMessage, setErrorMessage] = useState(false);
  const {
    filterData: { start_date, end_date },
  } = useContext(FilterContext);

  const fetchReport = useCallback(() => {
    setIsFetching(true);

    // Build query parameters
    const queryParams = [];

    if (start_date) {
      queryParams.push(`start_date=${start_date}`);
    }
    if (end_date) {
      queryParams.push(`end_date=${end_date}`);
    }

    // Add selected artists
    if (selectedArtists && selectedArtists.length > 0) {
      selectedArtists.forEach(artist => {
        queryParams.push(`artists[]=${artist.value}`);
      });
    }

    // Add selected event types
    if (selectedEventTypes && selectedEventTypes.length > 0) {
      selectedEventTypes.forEach(eventType => {
        queryParams.push(`event_types[]=${eventType.value}`);
      });
    }

    const queryString = queryParams.join("&");

    api
      .get(`/reports/artists_performance?${queryString}`)
      .then((r) => {
        setReport(r.data);
        setErrorMessage(false);
      })
      .catch((error) => {
        console.log("useArtistsPerformanceReport error", error);
        setErrorMessage("Failed to load artists performance data. Please try again or contact support if the problem persists.");
      })
      .finally(() => {
        setIsFetching(false);
      });
  }, [api, start_date, end_date, selectedArtists, selectedEventTypes]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  return {
    report,
    isFetching,
    errorMessage,
  };
};

const ArtistsPerformance = () => {
  const { filterData } = useContext(FilterContext);
  const { isAdmin } = useContext(UserContext);
  const printRef = useRef();
  const api = useApi();

  // State for filters
  const [artists, setArtists] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [selectedArtists, setSelectedArtists] = useState([]);
  const [selectedEventTypes, setSelectedEventTypes] = useState([]);

  // Date selections
  const { dateOptions, handleDateSelect, renderCustomDateSelector } = useDateSelections();

  // Report data
  const { report, isFetching, errorMessage } = useArtistsPerformanceReport(selectedArtists, selectedEventTypes);

  // Artist options for multi-select
  const artistOptions = artists.map((artist) => ({
    label: artist.active ? artist.name : `${artist.name} (inactive)`,
    value: artist.id,
  }));

  // Event type options for multi-select
  const eventTypeOptions = eventTypes.map((eventType) => ({
    label: eventType.name,
    value: eventType.id,
  }));

  // Handle artist selection
  const handleArtistSelect = (selectedOptions) => {
    setSelectedArtists(selectedOptions || []);
  };

  // Handle event type selection
  const handleEventTypeSelect = (selectedOptions) => {
    setSelectedEventTypes(selectedOptions || []);
  };

  // Load artists and event types on component mount
  useEffect(() => {
    api.get(`/artists`).then((r) => {
      if (isAdmin) {
        setArtists(r.data);
      } else {
        setArtists(r.data.filter((artist) => artist.active === true));
      }
    });

    api.get(`/event_types`).then((r) => {
      setEventTypes(r.data);
    });
  }, [api, isAdmin]);

  // Calculate totals from the artists data
  const calculateTotals = () => {
    if (!report?.artists || report.artists.length === 0) {
      return {
        gross_sales: 0,
        tax: 0,
        event_fees: 0,
        cc_fee: 0,
        hall_fee: 0,
        total_to_artist: 0,
        total_from_venue: 0,
        cogs: 0,
        profit_to_artist: 0,
        attendance: 0,
        per_cap: 0
      };
    }

    const totals = report.artists.reduce((acc, artist) => ({
      gross_sales: acc.gross_sales + (artist.gross_sales || 0),
      tax: acc.tax + (artist.tax || 0),
      event_fees: acc.event_fees + (artist.event_fees || 0),
      cc_fee: acc.cc_fee + (artist.cc_fee || 0),
      hall_fee: acc.hall_fee + (artist.hall_fee || 0),
      total_to_artist: acc.total_to_artist + (artist.total_to_artist || 0),
      total_from_venue: acc.total_from_venue + (artist.total_from_venue || 0),
      cogs: acc.cogs + (artist.cogs || 0),
      profit_to_artist: acc.profit_to_artist + (artist.profit_to_artist || 0),
      attendance: acc.attendance + (artist.attendance || 0),
    }), {
      gross_sales: 0,
      tax: 0,
      event_fees: 0,
      cc_fee: 0,
      hall_fee: 0,
      total_to_artist: 0,
      total_from_venue: 0,
      cogs: 0,
      profit_to_artist: 0,
      attendance: 0,
    });

    // Calculate the correct per-cap based on total gross sales and total attendance
    totals.per_cap = totals.attendance > 0 ? totals.gross_sales / totals.attendance : 0;

    return totals;
  };

  const totals = calculateTotals();

  // Prepare data for export
  const exportData = report?.artists?.map((artist) => ({
    Artist: artist.artist_name,
    Gross_Sales: artist.gross_sales,
    Tax: artist.tax,
    Event_Fees: artist.event_fees,
    CC_Fee: artist.cc_fee,
    Hall_Fee: artist.hall_fee,
    Total_To_Artist: artist.total_to_artist,
    Total_From_Venue: artist.total_from_venue,
    COGS: artist.cogs,
    Profit_To_Artist: artist.profit_to_artist,
    Attendance: artist.attendance,
    Per_Cap: artist.per_cap,
  })) || [];

  const totalsExport = {
    Artist: "Totals",
    Gross_Sales: totals.gross_sales,
    Tax: totals.tax,
    Event_Fees: totals.event_fees,
    CC_Fee: totals.cc_fee,
    Hall_Fee: totals.hall_fee,
    Total_To_Artist: totals.total_to_artist,
    Total_From_Venue: totals.total_from_venue,
    COGS: totals.cogs,
    Profit_To_Artist: totals.profit_to_artist,
    Attendance: totals.attendance,
    Per_Cap: totals.per_cap,
  };

  const combinedExportData = [...exportData, totalsExport];

  return (
    <ReportsLayout>
      <div className="mt-8 mx-8 py-10 px-12 bg-white rounded-lg border">
        {report?.artists?.length > 0 && (
          <div className="flex justify-end text-sm">
            <ReactToPrint
              trigger={() => <Button className="button" text="Print"></Button>}
              content={() => printRef.current}
            />
            <div className="ml-2">
              <Button
                className="button button-white"
                text="Export"
                onClick={() =>
                  exportToCSV(combinedExportData, "artists_performance")
                }
              ></Button>
            </div>
          </div>
        )}

        <div className="max-w-max">
          <Link to="/reports">
            <div className="-mt-6 mb-6 font-medium text-black text-sm">
              <FontAwesomeIcon icon={faArrowLeft}></FontAwesomeIcon> Back to reports
            </div>
          </Link>
        </div>

                {/* Filters Section */}
        <div className="mb-8">
          <div className="text-lg font-medium mb-4">Filters</div>

          {/* Date Filter */}
          <div className="mb-4">
            <div className="text-sm font-medium mb-2">Date Range</div>
            <div className="flex flex-col text-sm w-1/4">
              <span className="text-xs">SELECT DATE RANGE</span>
              <Select
                id="date"
                className="text-xs font-light w-full"
                placeholder="Select date range"
                options={dateOptions}
                onChange={handleDateSelect}
                value={dateOptions?.filter((each) => each.value === filterData.date_button)}
              />
            </div>
            <div className="my-4 w-1/2">{filterData.date_button === "custom" && renderCustomDateSelector()}</div>
          </div>

          {errorMessage && <div className="text-red-500 mb-4">{errorMessage}</div>}

          {/* Artists Filter */}
          <div className="mb-4">
            <div className="text-sm font-medium mb-2">Artists</div>
            <Select
              isMulti
              placeholder="Select artists..."
              options={artistOptions}
              value={selectedArtists}
              onChange={handleArtistSelect}
              className="w-full max-w-md"
            />
          </div>

          {/* Event Types Filter */}
          <div className="mb-4">
            <div className="text-sm font-medium mb-2">Event Types</div>
            <Select
              isMulti
              placeholder="Select event types..."
              options={eventTypeOptions}
              value={selectedEventTypes}
              onChange={handleEventTypeSelect}
              className="w-full max-w-md"
            />
          </div>
        </div>

        {/* Report Content */}
        <div ref={printRef} className="mt-8 mx-8 text-black">
          <div className="flex text-base mb-2 border-2 border-black p-2">
            <div className="w-1/3 text-xl font-extrabold mr-4">
              Artists Performance Report
            </div>
            <div className="w-1/3">
              <div className="text-lg flex self-center">
                <div className="mr-2 italic">Date Range:</div>
                {filterData.start_date && filterData.end_date ? (
                  `${filterData.start_date} to ${filterData.end_date}`
                ) : (
                  "All dates"
                )}
              </div>
            </div>
            <div className="w-1/3">
              <div className="self-center flex font-normal">
                <div className="mr-2 italic">Artists:</div>
                {selectedArtists.length > 0 ? selectedArtists.length : "All"}
              </div>
              <div className="self-center flex font-normal">
                <div className="mr-2 italic">Event Types:</div>
                {selectedEventTypes.length > 0 ? selectedEventTypes.length : "All"}
              </div>
            </div>
          </div>

          {report?.artists?.length > 0 && !isFetching && (
            <div>
              <table className="w-full">
                <thead className="text-xs font-light">
                  <tr className="text-right border-b-2 border-black h-11">
                    <td className="w-1/8 text-left pr-2">ARTIST</td>
                    <td className="w-1/16">GROSS SALES</td>
                    <td className="w-1/16">TAX</td>
                    <td className="w-1/16">EVENT FEES</td>
                    <td className="w-1/16">CC FEE</td>
                    <td className="w-1/16">HALL FEE</td>
                    <td className="w-1/16">TOTAL TO ARTIST</td>
                    <td className="w-1/16">TOTAL FROM VENUE</td>
                    <td className="w-1/16">COGS</td>
                    <td className="w-1/16">PROFIT TO ARTIST</td>
                    <td className="w-1/16">ATTENDANCE</td>
                    <td className="w-1/16">PER CAP</td>
                  </tr>
                </thead>
                <tbody>
                  {report.artists.map((artist) => (
                    <tr
                      key={artist.artist_id}
                      className="border-b border-black text-right text-sm h-10"
                    >
                      <td className="text-left pr-2">{artist.artist_name}</td>
                      <td>
                        {artist.gross_sales?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </td>
                      <td>
                        {artist.tax?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </td>
                      <td>
                        {artist.event_fees?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </td>
                      <td>
                        {artist.cc_fee?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </td>
                      <td>
                        {artist.hall_fee?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </td>
                      <td>
                        {artist.total_to_artist?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </td>
                      <td>
                        {artist.total_from_venue?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </td>
                      <td>
                        {artist.cogs?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </td>
                      <td>
                        {artist.profit_to_artist?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </td>
                      <td>{artist.attendance?.toLocaleString()}</td>
                      <td>
                        {artist.per_cap?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </td>
                    </tr>
                  ))}

                  {/* Totals Row */}
                  <tr className="text-right h-11 w-full text-xs font-bold">
                    <td className="text-left pr-2">TOTALS</td>
                    <td>
                      {totals.gross_sales?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </td>
                    <td>
                      {totals.tax?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </td>
                    <td>
                      {totals.event_fees?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </td>
                    <td>
                      {totals.cc_fee?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </td>
                    <td>
                      {totals.hall_fee?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </td>
                    <td>
                      {totals.total_to_artist?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </td>
                    <td>
                      {totals.total_from_venue?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </td>
                    <td>
                      {totals.cogs?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </td>
                    <td>
                      {totals.profit_to_artist?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </td>
                    <td>{totals.attendance?.toLocaleString()}</td>
                    <td>
                      {totals.per_cap?.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {isFetching && (
            <div className="mx-12 py-16">
              <div className="w-full flex justify-center h-full items-center">
                <FontAwesomeIcon icon={faSpinner} spin></FontAwesomeIcon>
              </div>
            </div>
          )}

          {(!report?.artists || report.artists.length === 0) && !isFetching && (
            <div className="mx-12 py-16">
              No data found for the selected filters. Please adjust your date range, artist selection, or event type selection.
            </div>
          )}
        </div>
      </div>
    </ReportsLayout>
  );
};

export default ArtistsPerformance;
